import * as THREE from 'three';
import type { Terrain } from '../world/Terrain';

export class CameraManager {
  public camera: THREE.PerspectiveCamera;
  public mode: 'orbit' | 'thirdPerson' | 'free' = 'orbit';

  // Orbit camera (tank mode)
  private orbitYaw = 0;
  private orbitPitch = 0.4;
  private orbitDistance = 25;
  private orbitTarget = new THREE.Vector3();

  // Third person camera (infantry mode)
  private tpYaw = 0;
  private tpPitch = 0.2;
  private tpDistance = 6;
  private tpOffset = new THREE.Vector3(1, 2, 0); // right shoulder offset
  private tpTarget = new THREE.Vector3();

  // Free camera (spectator)
  private freePos = new THREE.Vector3(0, 30, 0);
  private freeYaw = 0;
  private freePitch = -0.3;

  // Smoothing
  private currentPos = new THREE.Vector3();
  private smoothSpeed = 8;

  private terrain?: Terrain;
  private raycaster = new THREE.Raycaster();

  constructor() {
    this.camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, 30, 50);

    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
    });
  }

  public setTerrain(terrain: Terrain): void {
    this.terrain = terrain;
  }

  public setMode(mode: 'orbit' | 'thirdPerson' | 'free'): void {
    this.mode = mode;
  }

  /**
   * Handle mouse input for camera rotation.
   */
  public handleMouseInput(deltaX: number, deltaY: number, sensitivity: number = 0.002): void {
    switch (this.mode) {
      case 'orbit':
        this.orbitYaw -= deltaX * sensitivity;
        this.orbitPitch -= deltaY * sensitivity;
        this.orbitPitch = Math.max(0.1, Math.min(Math.PI / 2 - 0.1, this.orbitPitch));
        break;
      case 'thirdPerson':
        this.tpYaw -= deltaX * sensitivity;
        this.tpPitch -= deltaY * sensitivity;
        this.tpPitch = Math.max(-0.5, Math.min(1.2, this.tpPitch));
        break;
      case 'free':
        this.freeYaw -= deltaX * sensitivity;
        this.freePitch -= deltaY * sensitivity;
        this.freePitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, this.freePitch));
        break;
    }
  }

  public handleScroll(delta: number): void {
    if (this.mode === 'orbit') {
      this.orbitDistance += delta * 0.01;
      this.orbitDistance = Math.max(10, Math.min(60, this.orbitDistance));
    } else if (this.mode === 'thirdPerson') {
      this.tpDistance += delta * 0.005;
      this.tpDistance = Math.max(3, Math.min(12, this.tpDistance));
    }
  }

  /**
   * Update camera every frame.
   * @param targetPos — The position of the entity to follow.
   * @param dt — Delta time in seconds.
   */
  public update(targetPos: THREE.Vector3, dt: number): void {
    switch (this.mode) {
      case 'orbit':
        this.updateOrbit(targetPos, dt);
        break;
      case 'thirdPerson':
        this.updateThirdPerson(targetPos, dt);
        break;
      case 'free':
        this.updateFree(dt);
        break;
    }
  }

  private updateOrbit(target: THREE.Vector3, dt: number): void {
    this.orbitTarget.lerp(target, Math.min(1, this.smoothSpeed * dt));

    const x = this.orbitDistance * Math.cos(this.orbitPitch) * Math.sin(this.orbitYaw);
    const y = this.orbitDistance * Math.sin(this.orbitPitch);
    const z = this.orbitDistance * Math.cos(this.orbitPitch) * Math.cos(this.orbitYaw);

    const desiredPos = new THREE.Vector3(
      this.orbitTarget.x + x,
      this.orbitTarget.y + y,
      this.orbitTarget.z + z
    );

    // Prevent camera from going below terrain
    if (this.terrain) {
      const terrainH = this.terrain.getHeightAt(desiredPos.x, desiredPos.z);
      desiredPos.y = Math.max(desiredPos.y, terrainH + 2);
    }

    this.camera.position.lerp(desiredPos, Math.min(1, this.smoothSpeed * dt));
    this.camera.lookAt(this.orbitTarget);
  }

  private updateThirdPerson(target: THREE.Vector3, dt: number): void {
    this.tpTarget.lerp(
      new THREE.Vector3(target.x, target.y + 1.5, target.z),
      Math.min(1, this.smoothSpeed * dt)
    );

    // Camera position: behind and above target
    const x = this.tpDistance * Math.cos(this.tpPitch) * Math.sin(this.tpYaw);
    const y = this.tpDistance * Math.sin(this.tpPitch);
    const z = this.tpDistance * Math.cos(this.tpPitch) * Math.cos(this.tpYaw);

    const desiredPos = new THREE.Vector3(
      this.tpTarget.x + x + this.tpOffset.x * Math.cos(this.tpYaw),
      this.tpTarget.y + y + this.tpOffset.y,
      this.tpTarget.z + z + this.tpOffset.x * Math.sin(this.tpYaw)
    );

    // Spring arm: raycast from target to desired camera pos
    if (this.terrain) {
      const terrainH = this.terrain.getHeightAt(desiredPos.x, desiredPos.z);
      desiredPos.y = Math.max(desiredPos.y, terrainH + 1);
    }

    this.camera.position.lerp(desiredPos, Math.min(1, this.smoothSpeed * 1.5 * dt));
    this.camera.lookAt(this.tpTarget);
  }

  private updateFree(dt: number): void {
    // WASD fly-through (handled externally via moveFreeCam)
    this.camera.position.copy(this.freePos);

    const lookTarget = new THREE.Vector3(
      this.freePos.x + Math.sin(this.freeYaw) * Math.cos(this.freePitch),
      this.freePos.y + Math.sin(this.freePitch),
      this.freePos.z + Math.cos(this.freeYaw) * Math.cos(this.freePitch)
    );
    this.camera.lookAt(lookTarget);
  }

  public moveFreeCam(forward: number, right: number, speed: number, dt: number): void {
    const dx = Math.sin(this.freeYaw) * forward + Math.cos(this.freeYaw) * right;
    const dz = Math.cos(this.freeYaw) * forward - Math.sin(this.freeYaw) * right;
    this.freePos.x += dx * speed * dt;
    this.freePos.z += dz * speed * dt;
  }

  // Getters for external systems
  public getYaw(): number {
    return this.mode === 'thirdPerson' ? this.tpYaw : this.orbitYaw;
  }

  public getPitch(): number {
    return this.mode === 'thirdPerson' ? this.tpPitch : this.orbitPitch;
  }
}
